import { graphql } from 'app/generated/gql'

/**
 * Held SMS operations.
 *
 * A held message is a `Message` with a future `sendDate` (TRK-5656). There is no
 * ScheduledMessage entity; `sendSms` holds the message when given a sendDate,
 * and the poller dispatches it.
 *
 * `messages` returns held rows like any other, by decision: the API owner
 * declined an exclude-pending argument, on the grounds that a consumer which
 * does not want queued texts should filter for itself. So every caller states
 * the statuses it wants; see `excludeHeld` in `app/lib/heldMessages` for the
 * read paths that want the opposite.
 */
export const HELD_MESSAGES_QUERY = graphql(`
  query HeldMessages($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        text
        sendDate
        status
        errorCode
        contactId
        threadId
        to
        from
        sentByUserId
        createdDate
        read
      }
      total
      page
    }
  }
`)

/**
 * The status half of the Scheduled filter's search: what is still queued, plus
 * what did not go out and nobody has acknowledged.
 *
 * A cancelled or failed held row is the only thing that tells the user their
 * message did not send and why, and the spec sorts exactly those rows to the top
 * of the Scheduled list because they need a decision. A `status:pending` search
 * cannot return one, so that whole state would be unreachable there.
 *
 * Every clause is positive: a bare leading `NOT` inside a parenthesised group
 * matches nothing in this search backend.
 *
 * What bounds the not-sent rows is `read`, not a clock. These used to age out
 * 24 hours after creation, which meant a message that never went out stopped
 * being reported whether or not anyone had seen the notice. The API now creates
 * a notice unread and `markMessageRead` marks it seen (TRK-5945), so a notice
 * stays until someone dismisses it and disappears for everyone once they do.
 * A queued row is unread too, which is why only the not-sent branches test it.
 *
 * The two not-sent branches are separate on purpose, and each carries its own
 * copy of that test so no clause depends on how this backend ranks AND against
 * OR.
 *
 * - `cancelled` requires an `errorCode`. Two l's is a held-only status (see
 *   `HELD_CANCELLED_STATUS`), but a row cancelled by the user carries no code,
 *   and `isHeldRowToShow` drops those, so asking for them would spend the page
 *   budget on rows the list then throws away.
 * - `failed` requires `NO_PHONE`. Without that, this search pulls in every
 *   carrier failure and consent refusal at the location, which are ordinary
 *   sends rather than scheduled ones.
 *
 * Kept deliberately in step with `isHeldRowToShow`: the Scheduled filter INCLUDES
 * exactly the not-sent rows that the "what was said" paths exclude, so a queued
 * message that did not go out stays reachable in one place and invisible in the
 * other.
 */
export const HELD_OR_RECENT_NOT_SENT =
  // `sendDate:*` is what separates a scheduled message from a row the API left
  // pending after a send failed another way. Without it the list fills with rows
  // that were never queued and can never go out.
  `((status:pending AND sendDate:*)` +
  ` OR (status:cancelled AND errorCode:* AND read:false)` +
  ` OR (status:failed AND errorCode:NO_PHONE AND read:false))`

/**
 * Returns the Message rather than a Boolean, so the urql document cache
 * invalidates and any list of held messages updates without a reload.
 */
export const CANCEL_SCHEDULED_MESSAGE = graphql(`
  mutation CancelScheduledMessage($id: ID!) {
    cancelScheduledMessage(id: $id) {
      id
      status
      errorCode
      sendDate
    }
  }
`)
